import PropTypes from "prop-types";
import React from "react";
import { useTranslation } from "react-i18next";
import { Typography, Button, Box } from "@mui/material";

//Custom
import Modal from "../../components/Modal";
import LoadingButton from "../../components/Buttons/LoadingButton";
import Table from "../../components/ReactTable/Table";
import ButtonGroup from "../../components/Buttons/ButtonGroup";
import { convertDurationObjectToDays } from "../../utils/datetime";
import { referenceTypes } from "../../constants";

const columns = (t, assignmentType) => {
  return [
    {
      Header: t("table.columnHeader.change"),
      accessor: ({ changes }) => changes[0],
      Cell: ({
        row: {
          original: { changes }
        }
      }) => (
        <Box display="flex" flexDirection="column">
          {changes.map((change, index) => (
            <Typography key={index} variant="body2">
              {change}
            </Typography>
          ))}
        </Box>
      ),
      id: "changes"
    },
    {
      Header: t(
        `bulkAssignmentTool.assignmentSelector.type.items.${assignmentType}`
      ),
      accessor: "referenceName",
      sortType: "string"
    },
    {
      Header: t("table.columnHeader.title"),
      accessor: "courseName"
    },
    {
      Header: t("table.columnHeader.timeToComplete"),
      accessor: ({ daysToComplete }) =>
        convertDurationObjectToDays(daysToComplete),
      id: "daysToComplete",
      Cell: ({
        row: {
          original: {
            daysToComplete: { unit, value }
          }
        }
      }) => (
        <Typography sx={{ textTransform: "capitalize" }} variant="body2">
          {t(`duration.${unit}.withCount`, { count: value })}
        </Typography>
      )
    },
    {
      Header: t("table.columnHeader.recurrence"),
      accessor: ({ repeatInterval }) =>
        convertDurationObjectToDays(repeatInterval),
      Cell: ({
        row: {
          original: {
            repeatInterval: { unit, value }
          }
        }
      }) => (
        <Typography variant="body2" sx={{ textTransform: "capitalize" }}>
          {!value
            ? "\u2015"
            : t(`duration.${unit}.withCount`, { count: value })}
        </Typography>
      ),
      id: "repeatInterval"
    }
  ];
};

/**
 * Shows the admin a list of assignments that will be added for the employee so that they can
 * confirm their changes
 */
const PreviewChangesModal = ({
  assignments,
  assignmentType,
  onCancel,
  onSubmit,
  open,
  isSubmitting
}) => {
  const { t } = useTranslation();
  return (
    <Modal
      data-qa="bulk-assignment-tool-preview-modal"
      disableEnforceFocus
      showCloseIcon
      maxWidth="lg"
      fillHeight
      fullWidth
      dialogTitle={
        <Typography variant="h4" color="textPrimary">
          {t("bulkAssignmentTool.previewChangesTitle", {
            assignmentType: t(
              `bulkAssignmentTool.assignmentSelector.type.items.${assignmentType}`
            )
          })}
        </Typography>
      }
      dialogContent={
        <Box mt={1} display="flex" minHeight={300} flexDirection="column">
          <Typography variant="subtitle1" sx={{ mb: 3 }}>
            {t("bulkAssignmentTool.previewChangesMessage")}
          </Typography>
          <Table
            columns={columns(t, assignmentType)}
            data={assignments}
            maxHeight="unset"
            paginated
            manualPagination={false}
            manualSortBy={false}
            cellSize="medium"
          />
        </Box>
      }
      dialogActions={
        <ButtonGroup align="left" mt={1.5}>
          <Button color="primary" onClick={onCancel}>
            {t("cancelBtn")}
          </Button>
          <LoadingButton
            color="primary"
            variant="contained"
            onClick={onSubmit}
            disabled={isSubmitting}
            pending={isSubmitting}
            data-qa="bulk-assignment-tool-save-btn"
          >
            {t("saveBtn")}
          </LoadingButton>
        </ButtonGroup>
      }
      open={open}
      onClose={onCancel}
    />
  );
};

PreviewChangesModal.propTypes = {
  assignmentsToAdd: PropTypes.arrayOf(
    PropTypes.shape({
      changes: PropTypes.arrayOf(PropTypes.string),
      referenceName: PropTypes.string,
      courseName: PropTypes.string,
      daysToComplete: PropTypes.shape({
        unit: PropTypes.oneOf(["days", "months", "years"]),
        value: PropTypes.number
      }),
      repeatInterval: PropTypes.shape({
        unit: PropTypes.oneOf(["days", "months", "years"]),
        value: PropTypes.number
      }),
      id: PropTypes.string,
      trainingObjectId: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.string
      ]),
      referenceId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      referenceType: PropTypes.oneOf(Object.values(referenceTypes)),
      existingAssignmentId: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.string
      ])
    })
  ),
  assignmentType: PropTypes.oneOf(["position", "location", "group"]),
  onCancel: PropTypes.func,
  onSubmit: PropTypes.func,
  open: PropTypes.bool,
  isSubmitting: PropTypes.bool
};

export default PreviewChangesModal;
